import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { format, formatDistanceToNow } from 'date-fns';
import { useData } from '../contexts/DataContext';
import { useAuth } from '../contexts/AuthContext';
import NoteCard from '../components/NoteCard';
import MemberAttentionBadge from '../components/MemberAttentionBadge';
import StrategicGoals from '../components/StrategicGoals';
import MiniGoals from '../components/MiniGoals';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiArrowLeft, FiPlus, FiUser, FiMail, FiPhone, FiMap, FiCalendar, FiPackage, FiClock, FiFileText, FiSave, FiAlertTriangle, FiVideo } = FiIcons;

const MemberDetail = () => {
  const { memberId } = useParams();
  const navigate = useNavigate();
  const { members, getMemberNotes, addNote } = useData();
  const { user } = useAuth();

  const member = members.find(m => m.id.toString() === memberId.toString());
  const memberNotes = getMemberNotes(member?.id);

  const [showAddNote, setShowAddNote] = useState(false);
  const [noteData, setNoteData] = useState({
    title: '',
    content: '',
    category: 'General' // Keep default category but don't show in form
  });
  const [loading, setLoading] = useState(false);
  const [localTime, setLocalTime] = useState(null);
  const [timeLoading, setTimeLoading] = useState(false);

  // Fetch and calculate local time based on member location
  useEffect(() => {
    if (member && (member.city || member.state || member.country)) {
      const calculateLocalTime = () => {
        setTimeLoading(true);
        try {
          // Build location string for timezone lookup
          const locationParts = [member.city, member.state, member.country].filter(Boolean);
          if (locationParts.length === 0) {
            setLocalTime(null);
            setTimeLoading(false);
            return;
          }

          // Use timezone offset calculation based on location
          // This is a more reliable approach than external APIs
          const now = new Date();

          // Try to determine timezone using Intl.DateTimeFormat
          // This uses the browser's built-in timezone database
          const locationString = locationParts.join(',');
          let formatter;

          try {
            // Try with country code first if available
            if (member.country) {
              // Map common country names to ISO codes
              const countryMap = {
                'USA': 'US',
                'United States': 'US',
                'UK': 'GB',
                'United Kingdom': 'GB',
                'Australia': 'AU',
                'Canada': 'CA',
                'Germany': 'DE',
                'France': 'FR',
                'Spain': 'ES',
                'Italy': 'IT',
                'Japan': 'JP',
                'China': 'CN',
                'India': 'IN',
                'Brazil': 'BR',
                'Mexico': 'MX',
                'South Africa': 'ZA',
                'Russia': 'RU'
              };

              const countryCode = countryMap[member.country] || member.country;

              // If we have city/state and country code, try to get specific timezone
              if (member.city) {
                const cityOptions = {
                  timeZone: findTimeZone(member.city, member.state, countryCode),
                  hour12: true,
                  hour: 'numeric',
                  minute: 'numeric'
                };
                formatter = new Intl.DateTimeFormat('en-US', cityOptions);
              } else {
                // Just use country-based timezone
                formatter = new Intl.DateTimeFormat('en-US', {
                  timeZone: findTimeZoneByCountry(countryCode),
                  hour12: true,
                  hour: 'numeric',
                  minute: 'numeric'
                });
              }
            }
          } catch (err) {
            console.error('Error with timezone lookup:', err);
          }

          // If we couldn't create a formatter, use a fallback approach
          if (!formatter) {
            formatter = new Intl.DateTimeFormat('en-US', {
              hour12: true,
              hour: 'numeric',
              minute: 'numeric'
            });
          }

          const timeString = formatter.format(now);
          setLocalTime(timeString);
        } catch (error) {
          console.error('Error calculating local time:', error);
          setLocalTime(null);
        } finally {
          setTimeLoading(false);
        }
      };

      calculateLocalTime();
      // Update time every minute
      const intervalId = setInterval(calculateLocalTime, 60000);

      return () => clearInterval(intervalId);
    }
  }, [member]);

  // Helper function to find timezone by location
  const findTimeZone = (city, state, country) => {
    // Map major cities to their timezones
    const cityTimeZones = {
      'New York': 'America/New_York',
      'Los Angeles': 'America/Los_Angeles',
      'Chicago': 'America/Chicago',
      'Houston': 'America/Chicago',
      'Phoenix': 'America/Phoenix',
      'Philadelphia': 'America/New_York',
      'San Antonio': 'America/Chicago',
      'San Diego': 'America/Los_Angeles',
      'Dallas': 'America/Chicago',
      'San Jose': 'America/Los_Angeles',
      'Austin': 'America/Chicago',
      'Jacksonville': 'America/New_York',
      'San Francisco': 'America/Los_Angeles',
      'Columbus': 'America/New_York',
      'London': 'Europe/London',
      'Paris': 'Europe/Paris',
      'Berlin': 'Europe/Berlin',
      'Madrid': 'Europe/Madrid',
      'Rome': 'Europe/Rome',
      'Toronto': 'America/Toronto',
      'Montreal': 'America/Montreal',
      'Vancouver': 'America/Vancouver',
      'Sydney': 'Australia/Sydney',
      'Melbourne': 'Australia/Melbourne',
      'Tokyo': 'Asia/Tokyo',
      'Singapore': 'Asia/Singapore',
      'Hong Kong': 'Asia/Hong_Kong',
      'Dubai': 'Asia/Dubai',
      'Mumbai': 'Asia/Kolkata',
      'Delhi': 'Asia/Kolkata',
      'Sao Paulo': 'America/Sao_Paulo',
      'Mexico City': 'America/Mexico_City',
      'Cairo': 'Africa/Cairo',
      'Beijing': 'Asia/Shanghai',
      'Shanghai': 'Asia/Shanghai'
    };

    // State-based timezone mapping for US states
    const usStateTimeZones = {
      'Alabama': 'America/Chicago',
      'Alaska': 'America/Anchorage',
      'Arizona': 'America/Phoenix',
      'Arkansas': 'America/Chicago',
      'California': 'America/Los_Angeles',
      'Colorado': 'America/Denver',
      'Connecticut': 'America/New_York',
      'Delaware': 'America/New_York',
      'Florida': 'America/New_York',
      'Georgia': 'America/New_York',
      'Hawaii': 'Pacific/Honolulu',
      'Idaho': 'America/Denver',
      'Illinois': 'America/Chicago',
      'Indiana': 'America/New_York',
      'Iowa': 'America/Chicago',
      'Kansas': 'America/Chicago',
      'Kentucky': 'America/New_York',
      'Louisiana': 'America/Chicago',
      'Maine': 'America/New_York',
      'Maryland': 'America/New_York',
      'Massachusetts': 'America/New_York',
      'Michigan': 'America/Detroit',
      'Minnesota': 'America/Chicago',
      'Mississippi': 'America/Chicago',
      'Missouri': 'America/Chicago',
      'Montana': 'America/Denver',
      'Nebraska': 'America/Chicago',
      'Nevada': 'America/Los_Angeles',
      'New Hampshire': 'America/New_York',
      'New Jersey': 'America/New_York',
      'New Mexico': 'America/Denver',
      'New York': 'America/New_York',
      'North Carolina': 'America/New_York',
      'North Dakota': 'America/Chicago',
      'Ohio': 'America/New_York',
      'Oklahoma': 'America/Chicago',
      'Oregon': 'America/Los_Angeles',
      'Pennsylvania': 'America/New_York',
      'Rhode Island': 'America/New_York',
      'South Carolina': 'America/New_York',
      'South Dakota': 'America/Chicago',
      'Tennessee': 'America/Chicago',
      'Texas': 'America/Chicago',
      'Utah': 'America/Denver',
      'Vermont': 'America/New_York',
      'Virginia': 'America/New_York',
      'Washington': 'America/Los_Angeles',
      'West Virginia': 'America/New_York',
      'Wisconsin': 'America/Chicago',
      'Wyoming': 'America/Denver',
      'DC': 'America/New_York',
      'District of Columbia': 'America/New_York'
    };

    // First try exact city match
    if (city && cityTimeZones[city]) {
      return cityTimeZones[city];
    }

    // For US locations, try state-based lookup
    if (country === 'US' && state && usStateTimeZones[state]) {
      return usStateTimeZones[state];
    }

    // Fall back to country-based timezone
    return findTimeZoneByCountry(country);
  };

  // Helper function to find timezone by country
  const findTimeZoneByCountry = (country) => {
    const countryTimeZones = {
      'US': 'America/New_York',
      'CA': 'America/Toronto',
      'GB': 'Europe/London',
      'UK': 'Europe/London',
      'AU': 'Australia/Sydney',
      'DE': 'Europe/Berlin',
      'FR': 'Europe/Paris',
      'ES': 'Europe/Madrid',
      'IT': 'Europe/Rome',
      'JP': 'Asia/Tokyo',
      'CN': 'Asia/Shanghai',
      'IN': 'Asia/Kolkata',
      'BR': 'America/Sao_Paulo',
      'MX': 'America/Mexico_City',
      'RU': 'Europe/Moscow',
      'ZA': 'Africa/Johannesburg',
      'SG': 'Asia/Singapore',
      'AE': 'Asia/Dubai',
      'NZ': 'Pacific/Auckland',
      'AR': 'America/Argentina/Buenos_Aires',
      'CL': 'America/Santiago',
      'CO': 'America/Bogota',
      'PE': 'America/Lima'
    };

    return countryTimeZones[country] || 'Etc/UTC';
  };

  // Check if member needs attention (no sessions or last session > 30 days ago)
  const needsAttention = () => {
    if (!member.last_session) return true;
    const lastSession = new Date(member.last_session);
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
    return lastSession < thirtyDaysAgo;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Not recorded';
    return format(new Date(dateString), 'MMM d, yyyy');
  };

  const formatTimeAgo = (dateString) => {
    if (!dateString) return 'Never';
    return formatDistanceToNow(new Date(dateString), { addSuffix: true });
  };

  // Generate Microsoft Teams meeting link
  const generateTeamsLink = (email) => {
    // Encode email for URL
    const encodedEmail = encodeURIComponent(email);
    // Generate Teams meeting link
    // This creates a direct chat link with the member
    return `https://teams.microsoft.com/l/chat/0/0?users=${encodedEmail}`;
  };

  const handleNoteChange = (e) => {
    const { name, value } = e.target;
    setNoteData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmitNote = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await addNote({
        member_id: member.id,
        coach_id: user.id,
        coach_name: user.name,
        ...noteData
      });

      // Reset form and hide it
      setNoteData({
        title: '',
        content: '',
        category: 'General'
      });
      setShowAddNote(false);
    } catch (error) {
      console.error('Error adding note:', error);
    } finally {
      setLoading(false);
    }
  };

  if (!member) {
    return (
      <div className="p-6">
        <div className="text-center py-12">
          <p className="text-gray-500">Member not found</p>
          <Link to="/members" className="text-primary-600 hover:text-primary-700 mt-4 inline-block">
            ← Back to Members
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-8"
      >
        <div className="flex items-center space-x-4 mb-4">
          <Link
            to="/members"
            className="flex items-center space-x-2 text-gray-600 hover:text-gray-900 transition-colors"
          >
            <SafeIcon icon={FiArrowLeft} className="w-5 h-5" />
            <span>Back to Members</span>
          </Link>
        </div>
      </motion.div>

      <div className="flex flex-col lg:flex-row gap-6">
        {/* Member Profile Section */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          className="lg:w-1/4"
        >
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 sticky top-6">
            <div className="flex flex-col items-center mb-6">
              <div className="w-20 h-20 bg-primary-100 rounded-full flex items-center justify-center mb-4">
                <SafeIcon icon={FiUser} className="w-10 h-10 text-primary-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 text-center">{member.name}</h2>
              {/* Attention badge */}
              {needsAttention() && (
                <div className="mt-2">
                  <MemberAttentionBadge lastSession={member.last_session} />
                </div>
              )}
            </div>

            <div className="space-y-4">
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wider">Contact Information</p>
                <div className="mt-2 space-y-2">
                  <div className="flex items-center space-x-2 text-sm">
                    <SafeIcon icon={FiMail} className="w-4 h-4 text-gray-400" />
                    <a
                      href={`mailto:${member.email}`}
                      className="text-gray-700 hover:text-primary-600 transition-colors"
                    >
                      {member.email}
                    </a>
                  </div>
                  {member.phone && (
                    <div className="flex items-center space-x-2 text-sm">
                      <SafeIcon icon={FiPhone} className="w-4 h-4 text-gray-400" />
                      <a
                        href={`tel:${member.phone}`}
                        className="text-gray-700 hover:text-primary-600 transition-colors"
                      >
                        {member.phone}
                      </a>
                    </div>
                  )}
                  <div className="flex items-center space-x-2 text-sm">
                    <SafeIcon icon={FiVideo} className="w-4 h-4 text-gray-400" />
                    <a
                      href={generateTeamsLink(member.email)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-gray-700 hover:text-primary-600 transition-colors"
                    >
                      Microsoft Teams
                    </a>
                  </div>
                </div>
              </div>

              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wider">Location</p>
                <div className="mt-2 space-y-2">
                  {(member.city || member.state || member.country) && (
                    <div className="flex items-center space-x-2 text-sm">
                      <SafeIcon icon={FiMap} className="w-4 h-4 text-gray-400" />
                      <span className="text-gray-700">
                        {[member.city, member.state, member.country].filter(Boolean).join(', ')}
                      </span>
                    </div>
                  )}
                  {/* Local time display */}
                  {(member.city || member.state || member.country) && (
                    <div className="flex items-center space-x-2 text-sm mt-1">
                      <SafeIcon icon={FiClock} className="w-4 h-4 text-gray-400" />
                      {timeLoading ? (
                        <span className="text-gray-500">Calculating local time...</span>
                      ) : localTime ? (
                        <span className="text-gray-700">Local time: {localTime}</span>
                      ) : (
                        <span className="text-gray-500">Local time unavailable</span>
                      )}
                    </div>
                  )}
                </div>
              </div>

              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wider">Membership</p>
                <div className="mt-2 space-y-2">
                  <div className="flex items-center space-x-2 text-sm">
                    <SafeIcon icon={FiCalendar} className="w-4 h-4 text-gray-400" />
                    <span className="text-gray-700">Joined {formatDate(member.date_joined)}</span>
                  </div>
                  <div className={`flex items-center space-x-2 text-sm ${
                    needsAttention() ? 'text-red-600 font-medium' : 'text-gray-700'
                  }`}>
                    {needsAttention() ? (
                      <SafeIcon icon={FiAlertTriangle} className="w-4 h-4 text-red-500" />
                    ) : (
                      <SafeIcon icon={FiClock} className="w-4 h-4 text-gray-400" />
                    )}
                    <span>Last Session: {formatTimeAgo(member.last_session)}</span>
                  </div>
                </div>
              </div>

              {member.programs && member.programs.length > 0 && (
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wider">Programs</p>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {member.programs.map(program => (
                      <span
                        key={program}
                        className="px-2 py-1 bg-primary-50 text-primary-700 text-xs rounded-full flex items-center"
                      >
                        <SafeIcon icon={FiPackage} className="w-3 h-3 mr-1" />
                        {program}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <button
              onClick={() => setShowAddNote(!showAddNote)}
              className="flex items-center justify-center space-x-2 w-full mt-6 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
            >
              <SafeIcon icon={showAddNote ? FiArrowLeft : FiPlus} className="w-4 h-4" />
              <span>{showAddNote ? 'Cancel' : 'Add Session Notes'}</span>
            </button>
          </div>
        </motion.div>

        {/* Main Content Section */}
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          className="lg:w-3/4 space-y-6"
        >
          {/* Strategic Goals Section */}
          <StrategicGoals memberId={member.id} />

          {/* Mini-Goals Section */}
          <MiniGoals memberId={member.id} />

          {/* Notes Section */}
          {showAddNote ? (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Add New Session Notes</h3>
              <form onSubmit={handleSubmitNote} className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <SafeIcon icon={FiFileText} className="w-4 h-4 inline mr-2" />
                    Session Title
                  </label>
                  <input
                    type="text"
                    name="title"
                    value={noteData.title}
                    onChange={handleNoteChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="Enter a title for this session"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Session Notes
                  </label>
                  <textarea
                    name="content"
                    value={noteData.content}
                    onChange={handleNoteChange}
                    rows={12}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-y font-mono text-sm leading-relaxed"
                    placeholder="Write detailed notes about this session...

You can format your notes with:
- Line breaks for new paragraphs
- Multiple spaces for indentation
- Bullet points and lists
- All formatting will be preserved when saved"
                    required
                    style={{
                      whiteSpace: 'pre-wrap',
                      fontFamily: 'ui-monospace, SFMono-Regular, "SF Mono", Consolas, "Liberation Mono", Menlo, monospace'
                    }}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    💡 Tip: Line breaks and spacing will be preserved exactly as you type them
                  </p>
                </div>

                <div className="flex justify-end">
                  <button
                    type="submit"
                    disabled={loading}
                    className="flex items-center space-x-2 px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50"
                  >
                    {loading ? (
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                    ) : (
                      <>
                        <SafeIcon icon={FiSave} className="w-4 h-4" />
                        <span>Save Session Notes</span>
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          ) : (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200">
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-xl font-semibold text-gray-900">
                    Session History
                  </h2>
                  <button
                    onClick={() => setShowAddNote(true)}
                    className="flex items-center space-x-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
                  >
                    <SafeIcon icon={FiPlus} className="w-4 h-4" />
                    <span>Add Session Notes</span>
                  </button>
                </div>

                <div className="space-y-4">
                  {memberNotes.length > 0 ? (
                    memberNotes
                      .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
                      .map((note) => (
                        <NoteCard key={note.id} note={note} />
                      ))
                  ) : (
                    <div className="text-center py-12">
                      <SafeIcon icon={FiFileText} className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                      <p className="text-gray-500 mb-4">No session history yet for this member.</p>
                      <button
                        onClick={() => setShowAddNote(true)}
                        className="inline-flex items-center space-x-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
                      >
                        <SafeIcon icon={FiPlus} className="w-4 h-4" />
                        <span>Add First Session Notes</span>
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default MemberDetail;